/** @format */
/* eslint-disable @typescript-eslint/member-ordering */

import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as projectActions from '@store/actions/projects.actions';
import { distinctUntilChanged, filter, map, take, delay, withLatestFrom } from 'rxjs/operators';
import { ToasterService } from '@app/core/services/toaster.service';
import { UserService } from '@app/core/services/user.service';
import { SentryService } from '@app/core/services/sentry.service';
import { PROJECT_MEMBER_ROLE } from '@app/project-members/shared/project-member.model';
import { ProjectMemberService } from '@app/project-members/shared/services/project-member.service';
import {
  Token,
  TokenPayload,
  TokenActions,
  EventType,
  getReturnUrl,
  isTokenExpired,
} from '@app/tokens/shared/tokens.model';
import { TokensApiService } from './api/tokens-api.service';
import { environment } from 'src/environments/environment';

/*
EXAMPLE tokenIds for testing
9807d1c0-3b92-4fc4-96c9-ba438a18670d
{
  createdAt: "2021-10-11T21:21:52.120Z"
  email: "jd@filmstacker.com"
  id: "4c857e68-5c36-44d4-9856-e76847f4751e"
  projectId: "filmstacker-dev"
  projectRole: "CREW"
  userId: "jd"
}
f01babe5-0b95-4950-8a33-c3d384675e2b
40519f3f-3be2-411a-95e0-c3be60a554ec
*/
const DEBUG_LOGS = !environment.production; // 🔍 Debug: active in dev/staging only

/**
 * Clean the Token Value
 * only allow lower, upper, numbers and dashes
 * Based on https://ricardometring.com/javascript-replace-special-characters
 */
const cleanToken = (str: string = '') => {
  try {
    return str.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z-])/g, '');
  } catch (error) {
    console.warn(`[TokenService] cleanToken caught:`, error);
    return str;
  }
};

@Injectable({
  providedIn: 'root',
})
export class TokensService {
  private tokensApi = inject(TokensApiService);
  private userService = inject(UserService);
  private projectMemberService = inject(ProjectMemberService);
  private toaster = inject(ToasterService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private sentryService = inject(SentryService);
  private store = inject(Store);

  get tokenId$(): Observable<string> {
    return this._tokenId$.asObservable();
  }
  get token$(): Observable<Token> {
    return this._token$.asObservable();
  }
  get payload$(): Observable<TokenPayload> {
    return this._payload$.asObservable();
  }

  private _tokenId$: BehaviorSubject<string> = new BehaviorSubject('');
  private _token$: BehaviorSubject<Token | undefined> = new BehaviorSubject(undefined);
  private _payload$: BehaviorSubject<TokenPayload | undefined> = new BehaviorSubject(undefined);
  private _loadingToken = false;

  constructor() {
    this.tokenId$
      .pipe(
        map((id) => cleanToken(id)),
        filter((id) => !!id),
        distinctUntilChanged()
      )
      .subscribe(async (id) => {
        DEBUG_LOGS && console.log(`[TokensService] tokenId updated: '${id}'`);
        /**
         * T4.18: this try/catch is REQUIRED, not defensive padding.
         *
         * `tokensApi.getToken` resolves `false` only for a genuine
         * `resource_not_found`; it RETHROWS everything else - a 500, a
         * timeout, a dropped connection, an ad-blocked request
         * (tokens-api.service.ts:50). This callback is `async`, so without a
         * handler that rejection escapes as an unhandled promise rejection
         * and `_token$` never emits.
         *
         * That is invisible but not harmless. It is the first step of the P1
         * QR-invite path: `onboarding-accept` subscribes
         * `token$.pipe(filter(Boolean))`, so `tokenId` is never assigned, the
         * accept button stays `[disabled]="!tokenId || isTokenExpired"`
         * forever, and the page tells the user
         * "Sorry, this invite code is no longer valid." about an invite that
         * is perfectly valid - with no retry and no sign anything failed.
         *
         * Same defect class as T4.9 / T4.15 / T4.16: an async failure inside
         * a `.subscribe()` callback. Do not remove the handler; a bare
         * `await` here is the bug.
         */
        try {
          const data = await this.tokensApi.getToken(id);
          DEBUG_LOGS && console.log(`[TokensService] get token response:`, data);
          if (data && data.id) {
            this._token$.next(data);
            if (data.payload) {
              this._payload$.next({ ...data.payload, tokenId: data.id });
            }
          }
        } catch (error) {
          console.warn(`[TokensService] getToken caught:`, error);
          // Report it: a silent failure on the QR-invite path is undiagnosable.
          this.sentryService.captureError(error instanceof Error ? error : new Error(String(error)));
          // And say so, rather than leaving the user on a dead accept button.
          this.toaster.present(this.translate.instant('ERRORS.GENERIC_OOPS'));
        }
      });
  }

  setTokenId(val) {
    DEBUG_LOGS && console.log(`[TokensService] setTokenId: '${val}'`);
    try {
      const cleaned = cleanToken(val);
      if (cleaned) {
        this._tokenId$.next(cleaned);
      }
    } catch (error) {
      console.warn(`setToken caught:`, error);
    }
  }

  private setPayload(val) {
    DEBUG_LOGS && console.log(`[TokensService] setPayload:`, val);
    this._payload$.next(val);
  }

  /**
   * MVP-1173 Create PersistentToken
   */
  createPersistentToken({
    userId,
    userEmail,
    projectId,
    eventType,
  }: {
    userId: string;
    userEmail: string;
    projectId: string;
    eventType?: string;
  }): Promise<{ id: string; message: string }> {
    const payload = {
      message: this.translate.instant('EMAILS.ONBOARDING.INVITE_MESSAGE'),
      projectRoles: [
        {
          projectId,
          role: PROJECT_MEMBER_ROLE.CREW,
        },
      ],
      returnUrl: getReturnUrl(projectId),
    };
    const token: Token = new Token({
      // email,
      createdBy: userId,
      sentToEmail: userEmail,
      fromEmail: userEmail, // user that sent the token
      projectId,
      projectRole: PROJECT_MEMBER_ROLE.CREW,
      action: TokenActions.PersistentInvite,
      payload,
      persistToken: true,
      eventType: eventType || EventType.Projects,
    });
    // MVP-1183 initially, let's NOT expire these, awaiting MVP-1229
    // if (eventType !== EventType.Weddings) {
    //   token.expires = getExpires();
    // }

    return this.createToken(token);
  }

  /**
   * Token, TokenPayload
   */
  async createToken(token: Token): Promise<{ id: string; message: string }> {
    /*
      id: string; // PK
      createdAt: string;
      createdBy: string; // INDEX: user that sent the token
      sentToEmail?: string; //  index on email
      sentToUserId?: string; // keep the sentToUserId if we know it, but no index
      projectId: string; // keep this for easy scans
      action: TokenActions; // action that token represents
      payload: TokenPayload = {}; // payload will include what do when accepted
      accepted?: boolean; // we will delete the entry when accepted
      error: TokenErrors;
    */

    if (!token || !token.sentToEmail) {
      throw new Error('Missing To Email?');
    }
    token.isActive = true;

    return new Promise((resolve, reject) => {
      // const userId = await lastValueFrom(this.userService.userId$); //rxjs7
      try {
        this.userService.userId$.pipe(take(1)).subscribe(async (userId) => {
          try {
            token.createdBy = userId;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const res: any = await this.tokensApi.createToken(token);
            DEBUG_LOGS && console.log(`[Tokens] createToken response:`, res);
            if (res?.data?.id) {
              return resolve({
                ...res.data,
                message: this.translate.instant('TOKENS.CREATED_SUCCESSFULLY'),
              });
            } else {
              console.warn(`[Tokens] createToken response missing data.id?:`, res);
              return reject(new Error(this.translate.instant('TOKENS.ERROR_ON_CREATE')));
            }
          } catch (error) {
            console.warn(`[Tokens] createToken inner CAUGHT:`, error);
            return reject(error);
          }
        });
      } catch (error) {
        console.warn(`[Tokens] createToken outer CAUGHT:`, error);
        return reject(error);
      }
    });
  }

  /**
   * @todo ? change to project-member.service.applyTokenRoleForCurrentUser
   * filtering out if the token is expired or not active..
   * note: no usages expect a response
   */
  applyTokenToCurrentUser({ tokenId }) {
    // ========================================
    // 🔍 DEBUG LOGGING: Token Application Start
    // ========================================
    if (DEBUG_LOGS) {
      console.group(`🎟️ ApplyTokenToCurrentUser @ ${new Date().toISOString()}`);
      console.log('📥 Input:', { tokenId });
      console.log('⏰ Starting delay(1500) and token validation...');
    }

    this.token$
      .pipe(
        /**
         * T4.20: the filter for a REAL token MUST come before `take(1)`.
         *
         * `_token$` is a `BehaviorSubject` seeded with `undefined`, so it
         * replays that seed to every new subscriber. The original order was
         * `delay(1500) → take(1) → filter(...)`, which meant:
         *
         *   t=0     token$ replays `undefined`
         *   t=200   the real Token arrives from getToken (delayed to t=1700)
         *   t=1500  the DELAYED `undefined` reaches take(1) -> it emits that,
         *           then COMPLETES and unsubscribes upstream
         *   t=1500  filter drops the `undefined`
         *   t=1700  the real Token is never delivered - the stream is closed
         *
         * Net effect: the subscribe body never ran and the CREW role was
         * never granted. `delay` time-shifts an emission; it does not wait
         * for a different one, which is what the old
         * "need to wait for the token to be received" comment assumed.
         *
         * This silently defeated FIX 3: `confirm-account.page.ts:101-102`
         * calls `setTokenId()` and `applyTokenToCurrentUser()` back to back
         * in the same tick, so on the new-user QR path the token was ALWAYS
         * still in flight and the role was ALWAYS dropped. It also explains
         * the reported ~50% intermittency: if a token had already been
         * loaded by an earlier `setTokenId` (e.g. the accept page's
         * ngOnInit), the BehaviorSubject replayed a real value and the same
         * code worked.
         *
         * The `delay(1500)` is KEPT, but after `take(1)`, because it has a
         * second purpose: `projectMemberService.applyTokenRoleForCurrentUser`
         * reads `userService.userId$` itself and writes that value onto the
         * member row, so firing before auth state settles would persist an
         * empty userId.
         */
        filter((t) => !!t && !!t.id),
        take(1),
        delay(1500),
        // filtering out if the token is expired or not active
        filter((t) => !isTokenExpired(t)),
        withLatestFrom(this.userService.userId$)
      )
      .subscribe(async ([token, userId]) => {
        // ========================================
        // 🔍 DEBUG: Token Retrieved and Validated
        // ========================================
        if (DEBUG_LOGS) {
          console.group(`🔓 Token Retrieved @ ${new Date().toISOString()}`);
          console.log('Token Details:', {
            tokenId: token.id,
            projectId: token.projectId,
            projectRole: token.projectRole,
            action: token.action,
            eventType: token.eventType,
            isExpired: isTokenExpired(token),
          });
          console.log('User:', { userId });
          console.groupEnd();
        }

        // SENTRY PHASE 1: Track QR invite token application start
        this.sentryService.addBreadcrumb({
          category: 'qr-invite',
          message: 'Token application started',
          level: 'info',
          data: {
            tokenId: token.id,
            projectId: token.projectId,
            projectRole: token.projectRole,
            tokenAction: token.action,
            eventType: token.eventType,
            userId,
            timestamp: new Date().toISOString(),
          },
        });

        // SENTRY PHASE 3: Initialize QR invite context with timing
        try {
          sessionStorage.setItem(
            'qr_invite_context',
            JSON.stringify({
              tokenId: token.id,
              projectId: token.projectId,
              userId,
              returnUrl: getReturnUrl(token.projectId),
              timestamp: new Date().toISOString(),
              checkpoints: {
                tokenStart: Date.now(),
              },
              validation: {
                tokenIdConsistent: true,
                projectIdConsistent: true,
                userIdConsistent: true,
              },
            })
          );
        } catch (error) {
          console.warn('[QR Invite] Failed to initialize context in sessionStorage:', error);
        }

        try {
          /** persistToken logic now handled in api */
          const { projectId, projectRole } = token;
          
          // 🔍 DEBUG: Before adding member
          if (DEBUG_LOGS) {
            console.group(`👥 Adding Member to Project @ ${new Date().toISOString()}`);
            console.log('Parameters:', { projectId, projectRole });
            console.log('Calling ProjectMemberService.applyTokenRoleForCurrentUser...');
          }
          
          const result = await this.projectMemberService.applyTokenRoleForCurrentUser(projectId, projectRole);

          // 🔍 DEBUG: Member addition result
          if (DEBUG_LOGS) {
            console.log('✅ Result:', result);
            console.log('Result Type:', typeof result);
            console.log('Has Message:', !!(result as any)?.message);
            console.groupEnd();
          }

          // if this user was already a member, do something different
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (result && (result as any).message) {
            // 🔍 DEBUG: User already a member
            if (DEBUG_LOGS) {
              console.warn('⚠️ User Already Member:', (result as any).message);
            }
            // SENTRY PHASE 1: User was already a member
            this.sentryService.addBreadcrumb({
              category: 'qr-invite',
              message: 'User already a member of project',
              level: 'info',
              data: {
                tokenId: token.id,
                projectId,
                userId,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                message: (result as any).message,
              },
            });

            setTimeout(() => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              this.toaster.present((result as any).message);
            }, 900);
            this._tokenId$.next('');
            this._token$.next({ ...token, success: true, tokenApplied: tokenId, returnUrl: token.payload.returnUrl });

            // P1-FIX: refresh the project here too. The success path below dispatches loadById;
            // this branch used to `return` without it, so a user who joined via the project page
            // crew section BEFORE accepting the token kept a stale, pre-membership project entity
            // (mine[] never populated). addMemberToProjectExists$ does not cover it either — that
            // effect only fetches when the project is absent from the store, and since the projects
            // slice is persisted it is present. No delay needed: the membership row already exists.
            this.store.dispatch(projectActions.loadById({ id: projectId }));

            if (DEBUG_LOGS) {
              console.group(`🏁 Token Applied (Already Member) @ ${new Date().toISOString()}`);
              console.log('Token Applied:', tokenId);
              console.log('Return URL:', token.payload.returnUrl);
              console.groupEnd();
            }
            return;
          }

          // 🔍 DEBUG: Member added successfully
          if (DEBUG_LOGS) {
            console.group(`✅ Member Added Successfully @ ${new Date().toISOString()}`);
            console.log('Result:', result);
            console.log('Result Type:', typeof result, 'Has Result:', !!result);
            console.groupEnd();
          }

          DEBUG_LOGS && console.log(`[TokensService] applyTokenToCurrentUser applyTokenRoleForCurrentUser:`, result);

          // SENTRY PHASE 1: Member added successfully
          this.sentryService.addBreadcrumb({
            category: 'qr-invite',
            message: 'Member added successfully to project',
            level: 'info',
            data: {
              tokenId: token.id,
              projectId,
              projectRole,
              userId,
              resultType: typeof result,
              hasResult: !!result,
            },
          });

          // Refresh the project in the store so mineIds gets populated.
          // WRITE-RACE FIX: short delay lets the backend propagate the membership row
          // before we query — without this, loadById may return the project without the member yet.
          setTimeout(() => {
            this.store.dispatch(projectActions.loadById({ id: projectId }));
          }, 1200);

          // SENTRY PHASE 3: Update context with token completion timing
          try {
            const qrContext = sessionStorage.getItem('qr_invite_context');
            if (qrContext) {
              const context = JSON.parse(qrContext);
              context.checkpoints.tokenComplete = Date.now();
              sessionStorage.setItem('qr_invite_context', JSON.stringify(context));
            }
          } catch (error) {
            console.warn('[QR Invite] Failed to update token completion timing:', error);
          }

          // Toast success
          setTimeout(() => {
            this.toaster.present(this.translate.instant('TOKENS.APPLIED_SUCCESS'));
          }, 900);

          // apply this token, deal with persistance and deletion in the api
          this.tokensApi.applyToken(token);

          this._tokenId$.next('');
          this._token$.next({ ...token, success: true, tokenApplied: tokenId, returnUrl: token.payload.returnUrl });
          
          // ========================================
          // 🔍 DEBUG: Token Application Complete
          // ========================================
          if (DEBUG_LOGS) {
            console.group(`🏁 Token Application Complete @ ${new Date().toISOString()}`);
            console.log('✅ Token Applied:', tokenId);
            console.log('📍 Project ID:', token.projectId);
            console.log('👤 User Role:', token.projectRole);
            console.log('🔗 Return URL:', token.payload.returnUrl);
            console.log('⏱️ Total Time:', '~2s (1.5s delay + API call)');
            console.groupEnd();
            console.groupEnd(); // Close the initial ApplyTokenToCurrentUser group
          }
        } catch (error) {
          console.warn(`[TokensService] caught error:`, error);
          
          // 🔍 DEBUG: Token application error
          if (DEBUG_LOGS) {
            console.group(`❌ Token Application FAILED @ ${new Date().toISOString()}`);
            console.error('Error:', error);
            console.error('Token ID:', token?.id);
            console.error('Project ID:', token?.projectId);
            console.groupEnd();
            console.groupEnd(); // Close the initial ApplyTokenToCurrentUser group
          }

          // SENTRY PHASE 1: Token application failed
          this.sentryService.addBreadcrumb({
            category: 'qr-invite',
            message: 'Token application failed',
            level: 'error',
            data: {
              tokenId: token.id,
              projectId: token.projectId,
              userId,
              error: error instanceof Error ? error.message : String(error),
            },
          });

          this.toaster.present(this.translate.instant('ERRORS.GENERIC_OOPS'));
        }
      });
  }

  /**
   * List Tokens for ProjectId or by userId
   */
  listTokens({ projectId, userId }: { projectId?: string; userId?: string }) {
    return this.tokensApi.listTokens({ projectId, userId });
  }

  /**
   * Enable PersistentToken by tokenId
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enableToken({ tokenId, expires }: { tokenId: string; expires?: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService.userId$.pipe(take(1)).subscribe(async (userId) => {
        try {
          const res = await this.tokensApi.enableToken({ tokenId, expires, userId });
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  /**
   * Disable PersistentToken by tokenId
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disableToken({ tokenId, expires }: { tokenId: string; expires?: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService.userId$.pipe(take(1)).subscribe(async (userId) => {
        try {
          const res = await this.tokensApi.disableToken({ tokenId, expires, userId });
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  /**
   * Delete Token by ID
   * MemberInviteModal.confirmDeleteToken
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deleteToken(tokenId: string): Promise<any> {
    return this.tokensApi.deleteToken(tokenId);
  }

  /**
   * Update Token
   * Token, TokenPayload
   */
  async updateToken(token: Partial<Token>): Promise<{ id: string; message: string }> {
    /*
      id: string; // PK
      createdAt: string;
      createdBy: string; // INDEX: user that sent the token
      sentToEmail?: string; //  index on email
      sentToUserId?: string; // keep the sentToUserId if we know it, but no index
      projectId: string; // keep this for easy scans
      action: TokenActions; // action that token represents
      payload: TokenPayload = {}; // payload will include what do when accepted
      accepted?: boolean; // we will delete the entry when accepted
      eventType?: EventTypes?;
      error: TokenErrors;
      isActive?: boolean;
    */

    if (!token || !token.id) {
      throw new Error('Missing Id?');
    }

    return new Promise((resolve, reject) => {
      this.userService.userId$.pipe(take(1)).subscribe(async (userId) => {
        token.updatedBy = userId;
        try {
          // updateToken
          DEBUG_LOGS && console.log(`UpdateToken`, token);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const res: any = await this.tokensApi.update(token);
          DEBUG_LOGS && console.log(`[Tokens] updateToken response:`, res);
          if (res && res.data && res.data.id) {
            return resolve({
              // ...res,
              id: res.data.id,
              message: `Token updated successfully.`,
            });
          } else {
            console.warn(`[Tokens] updateToken response missing data.id?:`, res);
            throw new Error('Token was not updated successfully, please try again.');
          }
        } catch (error) {
          console.warn(`[Tokens] updateToken CAUGHT:`, error);
          reject(error);
        }
      });
    });
  }

  // /**
  //  * create method in TokenService to generate payload for invites
  //  */
  // generatePayloadForCrewInvites(crewInvites: ProjectRolePayload[] = []) {
  //   const payload = {};
  //   crewInvites.forEach(({ email, projectId, role = PROJECT_MEMBER_ROLE.CREW  }) => {
  //     // payload
  //   })
  // }
}
